import { useEffect, useRef, useState } from "react";
import { AiOutlineClose, AiOutlineUpload } from "react-icons/ai";
import { CiCircleQuestion } from "react-icons/ci";
import axios from "axios";
import apiEndpoints from "../apiconfig";
import { useSnackbar } from "../DynamicComponents/SnackbarContext";

const ImportContactModal = ({ onClose, onImport }) => {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("Upload CSV");
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [previewContacts, setPreviewContacts] = useState([]);
  const [groupOptions, setGroupOptions] = useState([]);
  const [groupSearch, setGroupSearch] = useState("");
  const [groupFocus, setGroupFocus] = useState(false);
  const { showSnackbar } = useSnackbar();

  // Fetch groups from backend
  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(apiEndpoints.getGroups, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.status && Array.isArray(res.data.data)) {
        setGroupOptions(res.data.data.map((g) => g.groupname));
      } else {
        setGroupOptions([]);
      }
    } catch (err) {
      console.error("Group fetch error:", err);
    }
  };

  const filteredGroups = groupOptions.filter((g) =>
    g.toLowerCase().includes(groupSearch.toLowerCase())
  );

  const handleAddGroup = async () => {
    if (!groupSearch.trim()) {
      showSnackbar("Enter group name", "error");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(apiEndpoints.addContact, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: "add_group",
          contact_group: groupSearch.trim(),
        }),
      });
      const data = await res.json();
      if (data.status) {
        showSnackbar("Group added successfully!", "success");
        fetchGroups();
        setSelectedGroup(groupSearch.trim());
        setGroupSearch("");
      } else {
        showSnackbar(data.message || "Failed to add group", "error");
      }
    } catch (err) {
      console.error("Add group error:", err);
      showSnackbar("Server error while adding group", "error");
    }
  };

  const handleFileClick = () => fileInputRef.current.click();

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      const selectedFile = e.target.files[0];
      setFile(selectedFile);
      setFileName(selectedFile.name);
      
      // Parse CSV for preview
      const reader = new FileReader();
      reader.onload = (event) => {
        const rows = event.target.result
          .split("\n")
          .filter((row) => row.trim() !== "");
        
        // Check if first row is header
        const hasHeader = rows[0]?.toLowerCase().includes('contact_name') || 
                         rows[0]?.toLowerCase().includes('mobile_number');
        
        const startIndex = hasHeader ? 1 : 0;
        const contacts = [];
        
        for (let i = startIndex; i < rows.length; i++) {
          if (rows[i].trim() === "") continue;
          
          const columns = rows[i].split(",");
          
          // Handle different CSV formats:
          // Format 1: contact_name, mobile_number, country_code, contact_group (optional)
          // Format 2: contact_name, country_code, mobile_number, contact_group (optional)
          // Format 3: contact_name, mobile_number, contact_group (optional)
          
          let contact_name = "", mobile_number = "", country_code = "+91", contact_group = "";
          
          if (columns.length >= 2) {
            // Try to detect format based on content
            const firstCol = columns[0]?.trim();
            const secondCol = columns[1]?.trim();
            const thirdCol = columns[2]?.trim();
            const fourthCol = columns[3]?.trim();
            
            // Check if second column looks like a mobile number
            const isMobileNumber = /^[0-9]{10,15}$/.test(secondCol);
            
            if (isMobileNumber) {
              // Format: name, mobile, country_code (optional), group (optional)
              contact_name = firstCol;
              mobile_number = secondCol;
              country_code = thirdCol && thirdCol !== '' ? thirdCol : "+91";
              contact_group = fourthCol || "";
            } else {
              // Format: name, country_code, mobile, group (optional)
              contact_name = firstCol;
              country_code = secondCol && secondCol !== '' ? secondCol : "+91";
              mobile_number = thirdCol || "";
              contact_group = fourthCol || "";
            }
            
            // If group is provided in CSV, use it; otherwise use selected group
            const finalGroup = contact_group || selectedGroup;
            
            contacts.push({
              contact_name: contact_name,
              country_code: country_code,
              mobile_number: mobile_number,
              contact_group: finalGroup
            });
          }
        }
        
        setPreviewContacts(contacts);
      };
      reader.readAsText(selectedFile);
    }
  };

  const handleImport = async () => {
    if (!file) {
      showSnackbar("Please upload a CSV file", "error");
      return;
    }

    if (previewContacts.length === 0) {
      showSnackbar("No valid contacts found to import", "error");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(apiEndpoints.addContact, {
        action: "import",
        contacts: previewContacts,
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.status) {
        const successCount = response.data.success_count || previewContacts.length;
        const errorCount = response.data.error_count || 0;
        
        let message = `Import completed: ${successCount} successful`;
        if (errorCount > 0) {
          message += `, ${errorCount} failed`;
        }
        
        showSnackbar(message, "success");
        
        if (onImport) onImport(previewContacts);
        onClose();
      } else {
        showSnackbar(response.data.message || "Import failed", "error");
      }
    } catch (err) {
      console.error("Error importing contacts:", err);
      showSnackbar("Import failed. Please check the file format.", "error");
    }
  };

  const downloadSampleCSV = () => {
    // Updated sample with group column (optional)
    const sample = `contact_name,mobile_number,country_code,contact_group
John Doe,1234567890,91,Family
Jane Smith,9876543210,91,Friends
Mike Johnson,5556667777,,Work
Sarah Wilson,1112223333,91,
Robert Brown,4445556666,44,UK Friends`;

    const blob = new Blob([sample], { type: "text/csv;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "sample_contacts.csv";
    link.click();
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-2xl rounded-xl shadow-xl border border-gray-100 overflow-hidden animate-fadeIn font-['Montserrat']">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 bg-[#25D366] text-white">
          <h2 className="text-lg font-semibold">Import Contacts</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-white/20 rounded-full transition"
          >
            <AiOutlineClose size={20} />
          </button>
        </div>
        
        {/* Body */}
        <div className="p-6 space-y-5">
          {/* GROUP SELECT WITH ADD BUTTON (Optional) */}
          <div className="relative w-full">
            <label className="text-sm font-medium text-gray-700">
              Default Group (Optional)
              <span className="text-gray-500 text-xs ml-1">
                - Will be used if group not specified in CSV
              </span>
            </label>
            <div className="mt-1 flex flex-col sm:flex-row sm:items-center gap-2">
              {/* Input for selecting or typing group */}
              <input
                type="text"
                placeholder="Select or enter default group (optional)"
                value={groupSearch}
                onChange={(e) => {
                  setGroupSearch(e.target.value);
                  setSelectedGroup(e.target.value);
                }}
                onFocus={() => setGroupFocus(true)}
                onBlur={() => setTimeout(() => setGroupFocus(false), 150)}
                className="flex-1 border border-gray-300 rounded-md px-3 py-2
                 focus:ring-2 focus:ring-[#25D366] outline-none w-full"
              />
              {/* ADD NEW GROUP BUTTON */}
              <button
                type="button"
                onClick={handleAddGroup}
                className="bg-[#25D366] text-white px-4 py-2 rounded-md text-sm
                 hover:bg-[#1CB85C] w-full sm:w-auto"
              >
                Add
              </button>
            </div>
            {/* GROUP DROPDOWN LIST */}
            {groupFocus && filteredGroups.length > 0 && (
              <ul
                className="absolute bg-white border border-gray-200 rounded-md mt-1
                   w-full max-h-36 overflow-y-auto shadow z-50"
              >
                {filteredGroups.map((g, i) => (
                  <li
                    key={i}
                    className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                    onClick={() => {
                      setGroupSearch(g);
                      setSelectedGroup(g);
                      setGroupFocus(false);
                    }}
                  >
                    {g}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* File Upload */}
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">
              Upload CSV File
            </label>
            <div
              onClick={handleFileClick}
              className="flex items-center justify-center w-full border-2 border-dashed border-gray-300 rounded-md p-6 text-gray-500 cursor-pointer hover:border-[#25D366] hover:text-[#25D366] transition"
            >
              <AiOutlineUpload className="mr-2 text-xl" />
              <span className="text-sm">{fileName}</span>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="hidden"
            />
            <p className="text-xs text-gray-500 mt-2">
              Supported formats: contact_name,mobile_number,country_code,contact_group
            </p>
          </div>

          {/* Preview */}
          {previewContacts.length > 0 && (
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-medium text-gray-700">
                  Preview ({previewContacts.length} contacts)
                </label>
                <span className="text-xs text-gray-500">
                  Groups from CSV will override default group
                </span>
              </div>
              <div className="border border-gray-200 rounded-md max-h-64 overflow-y-auto">
                <table className="w-full text-sm text-gray-700">
                  <thead className="bg-gray-100 font-medium sticky top-0">
                    <tr>
                      <th className="px-4 py-2 border-r">S.No</th>
                      <th className="px-4 py-2 border-r">Name</th>
                      <th className="px-4 py-2 border-r">Country Code</th>
                      <th className="px-4 py-2 border-r">Mobile Number</th>
                      <th className="px-4 py-2">Group</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewContacts.map((contact, i) => (
                      <tr
                        key={i}
                        className="border-t hover:bg-gray-50 transition"
                      >
                        <td className="px-4 py-2 border-r text-center">
                          {i + 1}
                        </td>
                        <td className="px-4 py-2 border-r">
                          {contact.contact_name}
                        </td>
                        <td className="px-4 py-2 border-r text-center">
                          {contact.country_code}
                        </td>
                        <td className="px-4 py-2 border-r">
                          {contact.mobile_number}
                        </td>
                        <td className="px-4 py-2">
                          {contact.contact_group || (
                            <span className="text-gray-400 italic">No group</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-between items-center">
          <button
            onClick={downloadSampleCSV}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm font-medium transition"
          >
            <CiCircleQuestion size={18} /> Sample CSV
          </button>
          <button
            onClick={handleImport}
            disabled={previewContacts.length === 0}
            className="px-6 py-2 bg-[#25D366] text-white rounded-md hover:bg-[#1CB85C] disabled:bg-gray-400 disabled:cursor-not-allowed text-sm font-semibold transition"
          >
            Import Contacts
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImportContactModal;